import type { GridApiPremium, GridCellParams, GridRowId, MuiEvent } from '@mui/x-data-grid-premium';
import type { KeyboardEvent } from 'react';

/**
 * Spreadsheet keyboard navigation for an editable grid's `onCellKeyDown`: Tab / Shift+Tab and the arrows move
 * between the given editable cells, wrapping to the next or previous row, and never leave the grid — MUI's own Tab
 * leaves the grid from a cell that is not being edited. `rowIds` are the rows in the order they are shown (a tree
 * grid passes its expanded rows); `editableFields` the columns to move among, in order — a grid with lanes (Plan
 * Entry's Total cells) passes the lane of the focused cell. Keys it does not handle reach MUI untouched.
 */
export function navigateEditableCells(
  api: GridApiPremium,
  params: GridCellParams,
  event: MuiEvent<KeyboardEvent>,
  rowIds: readonly GridRowId[],
  editableFields: readonly string[],
): void {
  const key = event.key;
  const isArrow = key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight';
  if (key !== 'Tab' && !isArrow) return;
  // Shift+Arrow = Excel-style range selection. MUI's `cellSelection` already implements it
  // on this very event (useGridCellSelection → selectCellRange), so hand it straight back.
  // We must NOT fall through: `moveTo` sets event.defaultMuiPrevented, and useGridEvent
  // gates EVERY internal cellKeyDown subscriber on that flag — so swallowing Shift+Arrow
  // here silently disabled range selection for the whole grid. (Shift+Tab is ours: it is
  // backward navigation, not selection.)
  if (isArrow && event.shiftKey) return;
  const rIdx = rowIds.indexOf(params.id);
  const eIdx = editableFields.indexOf(params.field);
  if (rIdx === -1 || eIdx === -1) return; // not on an editable cell (e.g. clicked a read-only cell)

  // Move into an editable cell, committing the current edit. Both Tab and the arrows only
  // ever target editable columns, so read-only cells are skipped entirely — you can't land
  // in one and get stuck.
  //
  // The target is left in VIEW mode on purpose. MUI's clipboard paste bails out when the
  // focused cell is in edit mode ("Do not paste data when the cell is in edit mode" —
  // useGridClipboardImport), so eagerly re-entering edit mode here disabled Ctrl+V across
  // the whole grid. Typing a character still starts edit mode and overwrites the value
  // (MUI's own canStartEditing), so the spreadsheet feel is unchanged.
  const moveTo = (nr: number, targetField: string) => {
    if (nr < 0 || nr >= rowIds.length || !targetField) return;
    event.defaultMuiPrevented = true;
    event.preventDefault();
    // ONLY when the source cell is actually editing. `stopCellEditMode` throws
    // ("MUI X: The cell ... is not in edit mode") on a view-mode cell, and since
    // defaultMuiPrevented/preventDefault are already set above, that exception would kill
    // the move AND MUI's own fallback navigation — arrows and Tab would do nothing at all.
    if (api.getCellMode(params.id, params.field) === 'edit') {
      api.stopCellEditMode({ id: params.id, field: params.field });
    }
    api.setCellFocus(rowIds[nr], targetField);
  };
  const step = (delta: number) => {
    let ne = eIdx + delta;
    let nr = rIdx;
    if (ne >= editableFields.length) { ne = 0; nr = rIdx + 1; }
    else if (ne < 0) { ne = editableFields.length - 1; nr = rIdx - 1; }
    moveTo(nr, editableFields[ne]);
  };

  // Arrows always move cell-to-cell (never step through the digits of the value), so
  // navigating with the keyboard stays fast. The value is selected on entry, so typing
  // overwrites it; positioning the caret inside a number is done by double-click/click.
  if (key === 'Tab') step(event.shiftKey ? -1 : 1);
  else if (key === 'ArrowUp') moveTo(rIdx - 1, params.field);
  else if (key === 'ArrowDown') moveTo(rIdx + 1, params.field);
  else if (key === 'ArrowLeft') step(-1);
  else step(1);
}
